import { useEffect, useState } from "react";
import type { Bootstrap, SavingsGoal } from "../../shared/types";
import { api } from "../lib/api";
import { formatMoney, formatDayMonthYear, todayISO, parseMoney } from "../lib/format";
import SavingsModal from "../components/SavingsModal";
import Modal from "../components/Modal";
import StatCard from "../components/StatCard";

// Whole months from date a to date b (b - a), floored at 0.
function monthsBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  let n = (by - ay) * 12 + (bm - am);
  if (bd < ad) n -= 1; // not a whole month yet
  return n;
}

export default function Savings({ data }: { data: Bootstrap | null }) {
  const [goals, setGoals] = useState<SavingsGoal[] | null>(null);
  const [modal, setModal] = useState(false);
  const [editGoal, setEditGoal] = useState<SavingsGoal | null>(null);
  const [addGoal, setAddGoal] = useState<SavingsGoal | null>(null);
  // Budget you've planned but not yet spent — subtracted from Safe to spend to
  // show what's genuinely free to save (mirrors the Budget page's tally).
  const [budgetUnspent, setBudgetUnspent] = useState<number | null>(null);

  async function load() {
    const r = await api.listSavings();
    setGoals(r.goals);
  }
  useEffect(() => {
    load();
    api.listBudget().then((b) => setBudgetUnspent(b.unspentMinor)).catch(() => setBudgetUnspent(0));
  }, []);

  if (!data) return <div className="state">Loading…</div>;

  const list = goals ?? [];
  const totalSaved = list.reduce((t, g) => t + g.currentMinor, 0);
  const totalTarget = list.reduce((t, g) => t + g.targetMinor, 0);

  async function afterSave() {
    setModal(false);
    await load();
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Savings goals</h1>
          <p className="page__lede">
            {list.length > 0
              ? `${formatMoney(totalSaved)} saved of ${formatMoney(totalTarget)} across ${list.length} goal${list.length === 1 ? "" : "s"}.`
              : "Set targets and track how much to put aside each month."}
          </p>
        </div>
        <button className="btn btn--primary" onClick={() => { setEditGoal(null); setModal(true); }}>+ Add goal</button>
      </div>

      {data.funds.safeToSpendMinor != null && (() => {
        const safe = data.funds.safeToSpendMinor;
        const realFree = budgetUnspent != null ? safe - budgetUnspent : null;
        return (
          <section className="stat-grid stat-grid--2">
            <StatCard
              label="Safe to spend this month"
              value={formatMoney(safe)}
              sub="After all your bills"
              tone={safe < 0 ? "negative" : undefined}
            />
            <StatCard
              label="Free to save"
              value={realFree != null ? formatMoney(realFree) : formatMoney(safe)}
              sub="Free to spend after budgeted spend"
              accent={realFree == null || realFree >= 0}
              tone={realFree != null && realFree < 0 ? "negative" : undefined}
            />
          </section>
        );
      })()}

      {goals == null ? (
        <div className="state">Loading…</div>
      ) : list.length === 0 ? (
        <div className="panel empty">No goals yet. Add one to start tracking a savings target.</div>
      ) : (
        <div className="goal-grid">
          {list.map((g) => {
            const pct = g.targetMinor > 0 ? Math.min(100, Math.round((g.currentMinor / g.targetMinor) * 100)) : 0;
            const remaining = Math.max(0, g.targetMinor - g.currentMinor);
            const monthsLeft = g.targetDate ? Math.max(0, monthsBetween(todayISO(), g.targetDate)) : null;
            const nowMonthly = monthsLeft == null ? null : monthsLeft <= 0 ? remaining : Math.ceil(remaining / monthsLeft);
            const planMonths = g.startDate && g.targetDate ? Math.max(1, monthsBetween(g.startDate, g.targetDate)) : null;
            const planMonthly = planMonths ? Math.ceil(g.targetMinor / planMonths) : null;
            const reached = g.currentMinor >= g.targetMinor;
            return (
              <section className="goal-card" key={g.id}>
                <div className="goal-card__head">
                  <div className="goal-card__title">
                    <span className="goal-card__name">{g.name}</span>
                    {g.bank && <span className="type-tag goal-card__bank">{g.bank}</span>}
                  </div>
                  <button className="icon-btn" onClick={() => { setEditGoal(g); setModal(true); }} aria-label="Edit goal" title="Edit">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
                  </button>
                </div>

                <div className="goal-card__figures">
                  <span className="goal-card__cur">{formatMoney(g.currentMinor)}</span>
                  <span className="goal-card__of">of {formatMoney(g.targetMinor)}</span>
                  <span className="goal-card__pct">{pct}%</span>
                </div>
                <div className="meter"><span style={{ width: `${pct}%`, background: reached ? "var(--color-status-green)" : "var(--color-primary)" }} /></div>

                <div className="goal-card__stats">
                  {reached ? (
                    <div className="goal-card__stat goal-card__stat--done"><strong>🎉 Goal reached</strong></div>
                  ) : (
                    <>
                      <div className="goal-card__stat">
                        <span>Need now</span>
                        <strong>{nowMonthly != null ? `${formatMoney(nowMonthly)}/mo` : `${formatMoney(remaining)} to go`}</strong>
                      </div>
                      {planMonthly != null && (
                        <div className="goal-card__stat">
                          <span>Planned</span>
                          <strong>{formatMoney(planMonthly)}/mo</strong>
                        </div>
                      )}
                      {monthsLeft != null && (
                        <div className="goal-card__stat">
                          <span>Time left</span>
                          <strong>{monthsLeft === 0 ? "Due now" : `${monthsLeft} mo`}</strong>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <p className="goal-card__foot">
                  {g.memberName && <>Held by {g.memberName}</>}
                  {g.memberName && g.targetDate && " · "}
                  {g.targetDate && <>by {formatDayMonthYear(g.targetDate)}</>}
                </p>
                {g.description && <p className="goal-card__desc">{g.description}</p>}

                <button className="btn btn--sm goal-card__add" onClick={() => setAddGoal(g)}>
                  + Add money
                </button>
              </section>
            );
          })}
        </div>
      )}

      {modal && <SavingsModal data={data} goal={editGoal} onClose={() => setModal(false)} onSaved={afterSave} />}
      {addGoal && (
        <AddMoneyModal
          goal={addGoal}
          onClose={() => setAddGoal(null)}
          onSaved={async () => { setAddGoal(null); await load(); }}
        />
      )}
    </div>
  );
}

// Quick top-up: add an amount to a goal's saved total without opening the full
// edit form. Re-uses the goal's existing values and only bumps currentMinor.
function AddMoneyModal({
  goal,
  onClose,
  onSaved,
}: {
  goal: SavingsGoal;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const remaining = Math.max(0, goal.targetMinor - goal.currentMinor);
  const added = parseMoney(amount);
  const newTotal = added != null && added > 0 ? goal.currentMinor + added : goal.currentMinor;

  async function save() {
    if (added == null || added <= 0) return setError("Enter an amount to add");
    setSaving(true);
    try {
      await api.updateSaving(goal.id, {
        name: goal.name,
        targetMinor: goal.targetMinor,
        currentMinor: goal.currentMinor + added,
        startDate: goal.startDate,
        targetDate: goal.targetDate,
        description: goal.description,
        memberId: goal.memberId,
        bank: goal.bank,
      });
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <Modal title={`Add to ${goal.name}`} onClose={onClose}>
      <div className="form">
        <p className="field__hint">
          Currently {formatMoney(goal.currentMinor)} of {formatMoney(goal.targetMinor)}
          {remaining > 0 ? ` · ${formatMoney(remaining)} to go` : " · reached 🎉"}
        </p>
        <label className="field">
          <span className="field__label">Amount to add (£)</span>
          <input
            className="input"
            inputMode="decimal"
            value={amount}
            autoFocus
            onChange={(e) => setAmount(e.target.value)}
            placeholder="100.00"
          />
        </label>
        <p className="field__hint">New total: <strong>{formatMoney(newTotal)}</strong></p>
        {error && <p className="form__error">{error}</p>}
        <div className="form__actions">
          <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" onClick={save} disabled={saving}>
            {saving ? "Adding…" : "Add money"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
